import { querySelectorOrThrow } from "../../utils/dom/querySelectorOrThrow";
import { IInitialisable } from "../../interfaces/IInitialisable";
import { Discounts } from "./Discounts";

export class PurchasePanel implements IInitialisable {
	private documentHelper = document;
	private querySelectorOrThrow = querySelectorOrThrow;

	private vatElement: HTMLElement;
	private priceElement: HTMLElement;
	private basePrice: number;

	// tested
	public Init() {
		const components = document.querySelectorAll<HTMLElement>('[data-cmpt="purchase-panel"].cmpt');
		if (components.length > 0) {
			this.setupComponent();
		}
	}

	// tested
	public setupComponent() {
		this.getBasePrice();
		this.initializeEventListeners();
		this.handleUpdate(0);
	}

	// tested
	private initializeEventListeners() {
		this.documentHelper.querySelectorAll(".js_addToBasket").forEach((AddToBasketForm) => {
			const quantityInput = AddToBasketForm.querySelector(".js_pp_quantity") as HTMLInputElement;
			const decrementButton = AddToBasketForm.querySelector(".js_pp_decrement") as HTMLButtonElement;
			const incrementButton = AddToBasketForm.querySelector(".js_pp_increment");
			const vatToggle: HTMLInputElement = this.documentHelper.querySelector(".js_pp_vat-toggle") as HTMLInputElement;
			const tabIndividual = this.querySelectorOrThrow<HTMLElement>(this.documentHelper, ".js_pp_tab--individual");
			const tabTeam = this.querySelectorOrThrow<HTMLElement>(this.documentHelper, ".js_pp_tab--team");

			if (decrementButton != null) {
				decrementButton.addEventListener("click", () => this.handleUpdate(-1));
			}

			if (incrementButton != null) {
				incrementButton.addEventListener("click", () => this.handleUpdate(1));
			}

			if (quantityInput != null && decrementButton != null) {
				quantityInput.addEventListener("change", () => this.setDisabledStatus(quantityInput, decrementButton));
			}

			if (vatToggle != null) {
				vatToggle.addEventListener("click", () => this.handleVatToggle(vatToggle));
			}

			tabIndividual.addEventListener("click", () => this.handleTabToggle(tabIndividual, tabTeam));
			tabTeam.addEventListener("click", () => this.handleTabToggle(tabTeam, tabIndividual));
		});
	}

	// tested
	private getApplicableDiscount(currentValue: number): number {
		for (const discount of this.discounts) {
			if (currentValue >= discount.threshold) {
				return discount.discount;
			}
		}
		return 0;
	}

	// tested
	private updatePriceDisplay(vatToggle: HTMLInputElement) {
		const currentValue = this.getCurrentValue(this.querySelectorOrThrow<HTMLInputElement>(this.documentHelper, ".js_pp_quantity"));
		const applicableDiscount = this.getApplicableDiscount(currentValue);

		const priceWithoutVat = this.basePrice * currentValue;
		const discountedPriceWithoutVat = priceWithoutVat * (1 - applicableDiscount);
		const discountedPriceWithVat = discountedPriceWithoutVat * 1.2;

		const displayedPrice = vatToggle.checked ? discountedPriceWithVat.toFixed(2) : discountedPriceWithoutVat.toFixed(0);

		this.priceElement.innerHTML = displayedPrice;
	}

	// tested
	private handleUpdate(increment: number) {
		const inputQuantity = this.documentHelper.querySelector(".js_pp_quantity") as HTMLInputElement;

		if (inputQuantity != null) {
			const vatToggle = this.querySelectorOrThrow<HTMLInputElement>(this.documentHelper, ".js_pp_vat-toggle");
			const decrementButton = this.documentHelper.querySelector(".js_pp_decrement") as HTMLButtonElement;

			const currentQuantity = this.getCurrentValue(inputQuantity);
			const newQuantity = currentQuantity + increment;
			inputQuantity.value = `${newQuantity}`;

			this.updatePriceDisplay(vatToggle);
			this.setDisabledStatus(inputQuantity, decrementButton);
		}
	}

	// tested
	private handleVatToggle(vatToggle: HTMLInputElement) {
		this.vatElement = this.querySelectorOrThrow(this.documentHelper, ".js_pp_vat");

		if (vatToggle.checked) {
			this.vatElement.style.display = "none";
		} else {
			this.vatElement.style.display = "inline-block";
		}

		this.updatePriceDisplay(vatToggle);
	}

	// tested
	private getCurrentValue(input: HTMLInputElement): number {
		const currentValue = parseInt(input.value, 10);
		return Number.isNaN(currentValue) ? 1 : currentValue;
	}

	// tested
	private getBasePrice() {
		this.priceElement = this.querySelectorOrThrow<HTMLElement>(this.documentHelper, ".js_pp_price");
		this.basePrice = parseFloat(this.priceElement.innerHTML.trim()) || 0;
	}

	// tested
	private setDiscountVisibility(discountElement: HTMLElement, isVisible: boolean) {
		discountElement.style.display = isVisible ? "block" : "none";
	}

	// tested
	private updateDiscountMessage(discountElementMsg: HTMLElement, message: string) {
		discountElementMsg.innerHTML = message;
	}

	// tested
	private applyDiscountVisibility(currentValue: number, discountElement: HTMLElement, discountMsg: HTMLElement) {
		const applicableDiscount = this.getApplicableDiscount(currentValue);
		let applicableDiscountMessage = "";

		for (const discount of this.discounts) {
			if (currentValue >= discount.threshold && discount.discount === applicableDiscount) {
				applicableDiscountMessage = discount.message;
				break;
			}
		}

		this.setDiscountVisibility(discountElement, applicableDiscountMessage !== "");

		if (applicableDiscountMessage) {
			this.updateDiscountMessage(discountMsg, applicableDiscountMessage);
		}
	}

	// tested
	private updateDecrementButton(decrementButton: HTMLButtonElement, isDisabled: boolean) {
		decrementButton.disabled = isDisabled;
		if (isDisabled) {
			decrementButton.classList.add("disabled");
		} else {
			decrementButton.classList.remove("disabled");
		}
	}

	// tested
	private applyDiscountAndButtonStatus(currentValue: number, decrementButton: HTMLButtonElement) {
		this.applyDiscountVisibility(currentValue, this.querySelectorOrThrow<HTMLElement>(this.documentHelper, ".js_pp_discount"), this.querySelectorOrThrow<HTMLElement>(this.documentHelper, ".purchase-panel_discount-msg"));

		this.updateDecrementButton(decrementButton, currentValue <= 1);
	}

	// tested
	private setDisabledStatus(input: HTMLInputElement, decrementButton: HTMLButtonElement) {
		const currentValue = this.getCurrentValue(input);
		if (currentValue <= 0) {
			input.value = "1";
		}
		this.applyDiscountAndButtonStatus(currentValue, decrementButton);
		this.updatePriceDisplay(this.querySelectorOrThrow<HTMLInputElement>(this.documentHelper, ".js_pp_vat-toggle"));
	}

	// tested
	private handleTabToggle(activeTab: HTMLElement, inactiveTab: HTMLElement) {
		activeTab.classList.add("active");
		inactiveTab.classList.remove("active");
	}

	private discounts: Discounts[] = [
		{ threshold: 500, message: "Includes a 40% discount for 500+ courses", discount: 0.4 },
		{ threshold: 100, message: "Includes a 30% discount for 100+ courses", discount: 0.3 },
		{ threshold: 50, message: "Includes a 20% discount for 50+ courses", discount: 0.2 },
		{ threshold: 10, message: "Includes a 10% discount for 10+ courses", discount: 0.1 },
	];
}
